import { useMemo } from "react"

import { createBrowserRouter, Navigate, RouterProvider } from "react-router-dom"

import { ErrorBoundary } from "@/components/error-boundary"
import { LoadingSection } from "@/components/loading-section"
import { RouteReadyGuard } from "@/components/route-ready-guard"
import { useRoutes } from "@/contexts/use-routes"
import { ProtectedRoute } from "@/features/auth/components/protected-route"
import { AuthErrorPage } from "@/features/auth/pages/auth-error-page"
import { AuthRedirectPage } from "@/features/auth/pages/auth-redirect-page"
import { M365ConnectionCallbackPage } from "@/features/auth/pages/m365-connection-callback-page"
import { M365ConnectionPage } from "@/features/auth/pages/m365-connection-page"
import { PersonalSourceCallbackPage } from "@/features/auth/pages/personal-source-callback-page"
import { SignedOutPage } from "@/features/auth/pages/signed-out-page"
import { OnboardingCoordinator } from "@/features/onboarding/components/onboarding-coordinator"
import { OnboardingPage } from "@/features/onboarding/pages/onboarding-page"
import { PersonalSourcesPage } from "@/features/personalization/pages/personal-sources-page"
import { PluginOAuthCallbackPage } from "@/features/plugins/oauth-callback-page"
import { PresenterViewApp } from "@/features/slide-sense/components/presentation/PresenterViewApp"
import { useInitSettings } from "@/hooks/use-init-settings"

import { AppLayout } from "../components/layouts/app-layout/app-layout"

export function Router() {
  const { routes, routeObjects, trainingRoutes } = useRoutes()
  const { isSettingsInitialized } = useInitSettings()

  // Wait for both settings and routes to be loaded before showing routeObjects
  const routesReady = isSettingsInitialized && routes.length > 0 && routeObjects.length > 0

  // Find the default route to redirect / to
  const defaultRoute = routes.find((r) => r.default_route)
  const redirectPath = defaultRoute?.path ?? "/explorer"

  // Memoize the router to prevent infinite loops in React Router v7.
  // In v7, createBrowserRouter should not be called on every render as it
  // causes state updates via startTransition, leading to re-render loops.
  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          // Root wrapper: having errorElement here ensures ANY error — including
          // React Router's own "No route found for path" when nothing matches at
          // the top level — renders our friendly ErrorBoundary instead of the
          // default raw stack trace.
          errorElement: <ErrorBoundary />,
          children: [
            {
              path: "/redirect",
              element: <AuthRedirectPage />,
            },
            {
              path: "/auth/m365/connection",
              element: (
                <ProtectedRoute>
                  <M365ConnectionPage />
                </ProtectedRoute>
              ),
            },
            {
              path: "/auth/m365/callback",
              element: (
                <ProtectedRoute>
                  <M365ConnectionCallbackPage />
                </ProtectedRoute>
              ),
            },
            {
              path: "/auth/personal-sources/callback",
              element: (
                <ProtectedRoute>
                  <PersonalSourceCallbackPage />
                </ProtectedRoute>
              ),
            },
            {
              path: "/settings/personal-sources",
              element: (
                <ProtectedRoute>
                  <PersonalSourcesPage />
                </ProtectedRoute>
              ),
            },
            {
              path: "/auth/error",
              element: <AuthErrorPage />,
            },
            {
              path: "/signed-out",
              element: <SignedOutPage />,
            },
            {
              // Slide Sense presenter window — opened in a second browser
              // window via `window.open()`. Mounts a minimal shell without
              // the app sidebar/header for a clean dual-pane presenter view.
              path: "/slide-sense/present",
              element: (
                <ProtectedRoute>
                  <RouteReadyGuard>
                    <OnboardingCoordinator defaultRoute={redirectPath} />
                  </RouteReadyGuard>
                </ProtectedRoute>
              ),
              children: [{ index: true, element: <PresenterViewApp /> }],
            },
            {
              path: "/",
              element: <Navigate replace to={redirectPath} />,
            },
            {
              path: "/",
              element: (
                <ProtectedRoute>
                  <RouteReadyGuard>
                    <OnboardingCoordinator defaultRoute={redirectPath} />
                  </RouteReadyGuard>
                </ProtectedRoute>
              ),
              children: [
                { path: "plugins/oauth/callback", element: <PluginOAuthCallbackPage /> },
                {
                  path: "onboarding",
                  element: <OnboardingPage defaultRoute={redirectPath} />,
                },
                {
                  element: <AppLayout />,
                  children: routesReady
                    ? [...routeObjects, trainingRoutes ?? {}]
                    : [
                        {
                          path: "*",
                          element: <LoadingSection />,
                        },
                      ],
                },
              ],
            },
          ],
        },
      ]),
    [redirectPath, routesReady, routeObjects, trainingRoutes]
  )

  // RouterProvider seeds its state once via useState(router.state) and only
  // adopts new state when the router emits (i.e. on a navigation). When we swap
  // in a freshly created router after settings load — going from the loading
  // stub to the real route tree — no navigation fires, so the provider stays
  // frozen on the stub's match and direct deep links (e.g. /admin, /slide-sense)
  // render a blank/limbo page until the user navigates. Keying on readiness
  // remounts the provider on that transition so it re-seeds from the real
  // router's match for the current URL. In-app dropdown navigation worked
  // already because it triggers a real navigation on the new router.
  return <RouterProvider key={routesReady ? "ready" : "loading"} router={router} />
}
